/**
 * Shared helpers for showing per-line customization answers (installation orders & add flow).
 */

import { parseCustomInformation } from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";

export type CustomizationFieldRow = { field_name?: string; field_type?: string; value?: unknown };

/** Some APIs use `Fields` or nest fields; normalize to an array. */
export function normalizeCustomizationFieldsList(raw: unknown): CustomizationFieldRow[] {
  if (Array.isArray(raw)) return raw as CustomizationFieldRow[];
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? (p as CustomizationFieldRow[]) : [];
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return Object.values(raw as Record<string, unknown>).filter(
      (x) => x && typeof x === "object"
    ) as CustomizationFieldRow[];
  }
  return [];
}

export function pickFieldsKeyFromCustomizationsBlock(
  block: Record<string, unknown> | null | undefined
): unknown {
  if (!block || typeof block !== "object") return undefined;
  const direct = (block as { fields?: unknown }).fields ?? (block as { Fields?: unknown }).Fields;
  if (direct !== undefined) return direct;
  const hit = Object.keys(block).find((k) => k.toLowerCase() === "fields");
  return hit ? (block as Record<string, unknown>)[hit] : undefined;
}

/** Prefer the row from `allRows` (e.g. selectedProducts) so we read the latest `customizations`. */
export function resolveSelectedLineItemForDisplay(item: any, allRows: any[] | null | undefined): any {
  if (!item || !Array.isArray(allRows)) return item;
  const sid = String(item?.slot_id ?? "").trim();
  const skey = String(item?.slotKey ?? "").trim();
  return (
    allRows.find(
      (p) =>
        p?.product_id === item.product_id &&
        String(p?.slot_id ?? "").trim() === sid &&
        String(p?.slotKey ?? "").trim() === skey
    ) ??
    allRows.find((p) => p?.product_id === item.product_id) ??
    item
  );
}

export function getCustomizationsBlockFromLineItem(row: any): { fields?: unknown } | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const block =
    (r.customizations as Record<string, unknown> | undefined) ??
    (r.Customizations as Record<string, unknown> | undefined) ??
    ((r.variant as Record<string, unknown> | undefined)?.customizations as Record<string, unknown> | undefined) ??
    null;
  if (!block || typeof block !== "object") return null;
  return block as { fields?: unknown };
}

/**
 * Only non-empty values; non-file fields A→Z by label; file/image fields last (A→Z).
 */
export function getVisibleCustomizationFieldsForDisplay(
  customizations: { fields?: unknown } | null | undefined
): CustomizationFieldRow[] {
  const fields = normalizeCustomizationFieldsList(
    pickFieldsKeyFromCustomizationsBlock(customizations as Record<string, unknown> | null)
  );
  if (fields.length === 0) return [];

  const byLabel = (a: CustomizationFieldRow, b: CustomizationFieldRow) =>
    String(a.field_name || "").localeCompare(String(b.field_name || ""), undefined, { sensitivity: "base" });

  const isFileLike = (f: CustomizationFieldRow) => {
    const t = String(f.field_type || "").toLowerCase();
    return t === "file" || t === "image";
  };

  const hasValue = (f: CustomizationFieldRow) => {
    const v = f.value;
    if (v == null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "number" || typeof v === "boolean") return true;
    return String(v).trim() !== "";
  };

  const filled = fields.filter(hasValue);
  const nonFiles = filled.filter((f) => !isFileLike(f)).sort(byLabel);
  const files = filled.filter(isFileLike).sort(byLabel);
  return [...nonFiles, ...files];
}

export type CustomInformationRow = { key: string; value: string };

/** Label/value rows from `custom_information` for inventory_source/type `custom`. */
export function getVisibleCustomInformationRowsForDisplay(row: any): CustomInformationRow[] {
  const src = String(row?.inventory_source ?? row?.inventory_type ?? "").trim().toLowerCase();
  if (src !== "custom") return [];

  const raw = String(row?.custom_information ?? "").trim();
  if (!raw) return [];

  const pairs = parseCustomInformation(raw);
  return pairs.length > 0 ? pairs : [{ key: "Details", value: raw }];
}
